import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography
} from '@mui/material';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import BookingTicketPdf from './BookingTicketPdf';
import { formatDateDisplay } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/currencyUtils';

// --- 1. ĐỊNH NGHĨA CUSTOM LABEL (Giống BookingForm) ---
const CustomLabel = ({ title, required = false }) => (
  <Typography
    variant="subtitle2"
    sx={{
      fontWeight: 'bold',
      mb: 0.5,
      color: '#2c3e50',
      fontSize: '0.9rem'
    }}
  >
    {title} {required && <span style={{ color: 'red' }}>*</span>}
  </Typography>
);

const TicketExportDialog = ({ open, onClose, bookingData }) => {
  // State lưu trữ dữ liệu hiển thị trên vé
  const [ticketData, setTicketData] = useState(null);

  // State trì hoãn hiển thị để fix lỗi màn hình trắng
  const [showPreview, setShowPreview] = useState(false);

  // Chờ hiệu ứng Dialog mở xong (300ms) rồi mới render PDF
  useEffect(() => {
      if (open) {
          setShowPreview(false);
          const timer = setTimeout(() => setShowPreview(true), 300);
          return () => clearTimeout(timer);
      }
  }, [open]);

  // Khi mở modal, copy dữ liệu từ bookingData vào ticketData
  useEffect(() => {
    if (bookingData && open) {
      setTicketData({
        ...bookingData,
        // Map dữ liệu mặc định
        customer_name_display: bookingData.customer ? bookingData.customer.name : '',
        tour_name_display: (bookingData.tours && bookingData.tours.length > 0) 
          ? bookingData.tours.map(t => `• ${t.custom_tour_name || t.tour_code_display || 'Tour'}`).join('\n') 
          : (bookingData.tour_group || bookingData.tour_name || ''),
        start_date_display: bookingData.start_date ? formatDateDisplay(bookingData.start_date) : '',
        transfer_display: (bookingData.transports && bookingData.transports.length > 0) ? 'Yes' : 'No',
        address_display: (bookingData.transports && bookingData.transports.length > 0) ? bookingData.transports[0].pickup_point : '',
        total_price_display: formatCurrency(bookingData.total_price),
        // Logic hiển thị text trạng thái thanh toán
        payment_status_text: bookingData.payment_status === 'PAID' ? 'Paid in Full' : (bookingData.payment_status === 'DEPOSIT' ? 'Deposited' : 'Pay Later / On Arrival'),
        special_request: bookingData.special_request || ''
      });
    }
  }, [bookingData, open]);

  // Hàm xử lý thay đổi input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setTicketData(prev => ({ ...prev, [name]: value }));
  };

  if (!ticketData) return null;

return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: '#007373', color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>
        Xuất vé điện tử (E-Ticket)
      </DialogTitle>

      {/* CHIA LAYOUT LEFT (FORM) - RIGHT (PREVIEW) */}
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'row', gap: 3, height: '80vh', p: 2 }}>
        
        {/* CỘT TRÁI: FORM NHẬP LIỆU (Chiếm 40% chiều rộng) */}
        <Box sx={{ width: '40%', display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto', pr: 1 }}>
          <Box>
            <CustomLabel title="Tên khách hàng / Đoàn" />
            <TextField size="small" fullWidth name="customer_name_display" value={ticketData.customer_name_display || ''} onChange={handleChange} />
          </Box>

          <Box>
            <CustomLabel title="Chương trình Tour" />
            <TextField size="small" fullWidth multiline rows={2} name="tour_name_display" value={ticketData.tour_name_display || ''} onChange={handleChange} />
          </Box>

          <Box>
            <CustomLabel title="Ngày khởi hành" />
            <TextField size="small" fullWidth name="start_date_display" value={ticketData.start_date_display || ''} onChange={handleChange} />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box flex={1}>
              <CustomLabel title="Transfer (Yes/No)" />
              <TextField size="small" fullWidth name="transfer_display" value={ticketData.transfer_display || ''} onChange={handleChange} />
            </Box>
            <Box flex={2}>
              <CustomLabel title="Địa chỉ (Address)" />
              <TextField size="small" fullWidth multiline rows={1} name="address_display" value={ticketData.address_display || ''} onChange={handleChange} />
            </Box>
          </Box>

          <Box>
            <CustomLabel title="Tổng tiền" />
            <TextField size="small" fullWidth name="total_price_display" value={ticketData.total_price_display || ''} onChange={handleChange} />
          </Box>

          <Box>
            <CustomLabel title="Trạng thái Thanh toán" />
            <TextField size="small" fullWidth name="payment_status_text" value={ticketData.payment_status_text || ''} onChange={handleChange} />
          </Box>

          <Box>
            <CustomLabel title="Yêu cầu đặc biệt / Ghi chú thêm" />
            <TextField size="small" fullWidth multiline rows={2} name="special_request" value={ticketData.special_request || ''} onChange={handleChange} />
          </Box>

        </Box>

        {/* CỘT PHẢI: PREVIEW PDF (Chiếm 60% chiều rộng) */}
        <Box sx={{ width: '60%', height: '100%', border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden' }}>
          {showPreview ? (
            <PDFViewer width="100%" height="100%" style={{ border: 'none' }}>
              <BookingTicketPdf data={{ ...ticketData }} />
            </PDFViewer>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', bgcolor: '#f5f5f5', color: '#666' }}>
               <Typography>Đang tải bản xem trước...</Typography>
            </Box>
          )}
        </Box>

      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose} variant="outlined" color="inherit" sx={{ fontWeight: 'bold' }}>
          HỦY
        </Button>

        <PDFDownloadLink
          document={<BookingTicketPdf data={{ ...ticketData }} />}
          fileName={`[Ticket]_${ticketData.booking_code}_${ticketData.customer_name_display || 'Guest'}.pdf`}
          style={{ textDecoration: 'none' }}
        >
          {({ loading }) => (
            <Button variant="contained" disabled={loading} sx={{ bgcolor: '#007373', '&:hover': { bgcolor: '#005c5c' }, fontWeight: 'bold' }}>
              {loading ? 'ĐANG TẠO...' : 'TẢI XUỐNG VÉ PDF'}
            </Button>
          )}
        </PDFDownloadLink>
      </DialogActions>
    </Dialog>
  );
};

export default TicketExportDialog;